import { Article, ForkKnife, Headphones, Video, Wind } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { SmoothImage } from "../../primitives/smooth-image";
import { CARD_EYEBROW } from "../../primitives/type";
import { PANEL_LABEL } from "./panel";
import type { AttentionContentPreviewItem, OpacityMix } from "./types";

const KIND_LABEL: Record<AttentionContentPreviewItem["kind"], string> = {
  recipe: "Recipe",
  guide: "Guide",
  article: "Article",
  reset: "Reset",
  podcast: "Podcast",
};

const KIND_ICON = {
  recipe: ForkKnife,
  guide: Video,
  article: Article,
  reset: Wind,
  podcast: Headphones,
} as const;

function hasContentPreviewImage(item: AttentionContentPreviewItem): boolean {
  return Boolean(item.imageUrl?.trim());
}

/** Put an item with a poster first so the featured tile is never an empty frame. */
export function arrangeContentPreviewItems(
  items: AttentionContentPreviewItem[],
): AttentionContentPreviewItem[] {
  const heroAt = items.findIndex(hasContentPreviewImage);
  if (heroAt <= 0) return items;
  const hero = items[heroAt];
  if (!hero) return items;
  return [hero, ...items.filter((_, index) => index !== heroAt)];
}

export function AttentionContentPreview({
  isLight,
  items,
  opacityMix,
  pending = false,
}: {
  isLight: boolean;
  items: AttentionContentPreviewItem[];
  opacityMix: OpacityMix;
  pending?: boolean;
}) {
  if (!pending && items.length === 0) return null;

  const mosaic = arrangeContentPreviewItems(items);
  const count = pending ? 4 : mosaic.length;
  const useHero = !pending && mosaicUsesHero(mosaic);

  return (
    // Deliberately not the bordered AttentionPanel the signal rows and the
    // look-back use: a poster grid inside a box reads as a box in a box.
    <section
      className="relative z-10 mt-3"
      aria-label="In your plan"
      aria-busy={pending || undefined}
    >
      <p className={cn(PANEL_LABEL, "m-0 mb-2")} style={{ color: opacityMix(70) }}>
        In your plan
      </p>
      {pending ? (
        <PreviewSkeleton isLight={isLight} opacityMix={opacityMix} />
      ) : (
        <ul className={cn("m-0 grid list-none gap-2 p-0", mosaicGridClass(count, useHero))}>
          {mosaic.map((item, index) => (
            <li
              key={`${item.kind}:${item.title}`}
              className={mosaicTileClass(index, count, useHero)}
            >
              <PreviewTile
                featured={useHero && index === 0}
                isLight={isLight}
                item={item}
                opacityMix={opacityMix}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function mosaicUsesHero(items: AttentionContentPreviewItem[]): boolean {
  const first = items[0];
  return items.length >= 3 && Boolean(first && hasContentPreviewImage(first));
}

function mosaicGridClass(count: number, useHero: boolean): string {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  if (count === 3) return "grid-cols-2 grid-rows-2";
  if (count === 4) return "grid-cols-2";
  if (useHero) return "grid-cols-4 grid-rows-2";
  return "grid-cols-3";
}

function mosaicTileClass(index: number, count: number, useHero: boolean): string {
  if (!useHero || index !== 0) return "";
  if (count === 3) return "row-span-2";
  if (count >= 5) return "col-span-2 row-span-2";
  return "";
}

function PreviewTile({
  featured,
  isLight,
  item,
  opacityMix,
}: {
  featured: boolean;
  isLight: boolean;
  item: AttentionContentPreviewItem;
  opacityMix: OpacityMix;
}) {
  const Icon = KIND_ICON[item.kind];
  const imageUrl = item.imageUrl?.trim() ? item.imageUrl : null;

  return (
    <figure
      className={cn(
        "relative m-0 h-full min-h-0 overflow-hidden rounded-lg",
        featured ? "h-full min-h-[168px]" : "h-[128px] sm:h-[140px]",
      )}
      style={{ background: opacityMix(isLight ? 8 : 12) }}
    >
      {imageUrl ? (
        <SmoothImage src={imageUrl} alt="" frameClassName="absolute inset-0 bg-transparent" />
      ) : (
        <span
          aria-hidden="true"
          className="absolute inset-0 grid place-items-center"
          style={{ color: isLight ? "var(--color-teal-700)" : "var(--color-paper)" }}
        >
          <Icon size={22} weight="duotone" />
        </span>
      )}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, transparent 38%, color-mix(in srgb, var(--color-navy-deep) 72%, transparent) 100%)",
        }}
      />
      <figcaption className="absolute inset-x-0 bottom-0 z-[1] px-2.5 pb-2 pt-6">
        <span className={cn(CARD_EYEBROW, "block text-paper/85")}>{KIND_LABEL[item.kind]}</span>
        <span
          className={cn(
            "mt-0.5 line-clamp-2 font-sans font-medium leading-snug tracking-tight text-paper",
            featured ? "text-sm" : "text-xs",
          )}
        >
          {item.title}
        </span>
      </figcaption>
    </figure>
  );
}

function PreviewSkeleton({ isLight, opacityMix }: { isLight: boolean; opacityMix: OpacityMix }) {
  return (
    <ul className="m-0 grid list-none grid-cols-2 gap-2 p-0">
      {(["a", "b", "c", "d"] as const).map((slot) => (
        <li key={slot}>
          <div
            className="h-[128px] rounded-lg sm:h-[140px]"
            style={{ background: opacityMix(isLight ? 8 : 14) }}
          />
        </li>
      ))}
    </ul>
  );
}
